import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye, Zap, Heart,
  Shield, Trophy,
  ChevronDown, ChevronUp, ChevronLeft, Search,
  BookOpen, ArrowRight, Flame
} from "lucide-react";

/* ── BADGE DEFINITIONS for help page ── */
const BADGE_CATEGORIES = [
  {
    label: "Supporter milestones",
    emoji: "🏆",
    color: "linear-gradient(135deg,#8b5cf6,#6d28d9)",
    description: "Earned by creators as their supporter community grows.",
    badges: [
      { emoji: "🌱", name: "10 Supporters",  how: "Reach 10 paid supporters on your profile." },
      { emoji: "⚡", name: "25 Supporters",  how: "Reach 25 paid supporters." },
      { emoji: "🔥", name: "50 Supporters",  how: "Reach 50 paid supporters." },
      { emoji: "💎", name: "100 Supporters", how: "Reach 100 paid supporters — a major milestone." },
      { emoji: "👑", name: "250 Supporters", how: "Reach 250 paid supporters — elite status." },
      { emoji: "🚀", name: "500 Supporters", how: "Reach 500 paid supporters — legendary." },
    ]
  },
  {
    label: "Content badges",
    emoji: "📸",
    color: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    description: "Earned by creators for publishing and reaching view milestones.",
    badges: [
      { emoji: "🎯", name: "First Moment",   how: "Post your first moment to the platform." },
      { emoji: "📸", name: "10 Moments",     how: "Publish 10 moments total." },
      { emoji: "💯", name: "100 Views",      how: "Earn 100 total views across all your moments." },
      { emoji: "👁",  name: "1K Views",       how: "Earn 1,000 total views — real reach." },
      { emoji: "🔥", name: "First Drop",     how: "Schedule your first drop." },
      { emoji: "⚡", name: "5 Drops",        how: "Complete 5 scheduled drops." },
    ]
  },
  {
    label: "Engagement badges",
    emoji: "💬",
    color: "linear-gradient(135deg,#ec4899,#be185d)",
    description: "Earned when your community starts engaging with your content.",
    badges: [
      { emoji: "💬", name: "First Comment",   how: "Receive your first comment on a moment." },
      { emoji: "❤️", name: "100 Likes",       how: "Earn 100 total likes across all moments." },
      { emoji: "🔖", name: "First Save",      how: "Have someone save one of your moments." },
      { emoji: "📢", name: "First Broadcast", how: "Send your first creator broadcast." },
      { emoji: "🗣",  name: "10 Broadcasts",   how: "Send 10 broadcasts to your followers." },
    ]
  },
  {
    label: "Loyalty badges",
    emoji: "📅",
    color: "linear-gradient(135deg,#22c55e,#15803d)",
    description: "Earned for sticking around and staying consistent.",
    badges: [
      { emoji: "🌱", name: "Early Adopter",    how: "Join during the beta period — founding generation." },
      { emoji: "🏆", name: "30-Day Streak",    how: "Log in 30 days in a row." },
      { emoji: "📅", name: "6 Months",         how: "Be active on 100ViewsOnly for 6 months." },
      { emoji: "🎂", name: "1 Year",           how: "Celebrate your one year anniversary on the platform." },
      { emoji: "🍁", name: "Canadian Creator", how: "Verified Canadian creator — you're what this platform was built for." },
    ]
  },
  {
    label: "Creator economy",
    emoji: "💰",
    color: "linear-gradient(135deg,#f59e0b,#d97706)",
    description: "Earned by creators who monetize their work.",
    badges: [
      { emoji: "💰", name: "First Supporter", how: "Earn your first paid supporter — it's real now." },
      { emoji: "🎉", name: "First Tip",       how: "Receive your first tip from a fan." },
      { emoji: "🏅", name: "First Tier",      how: "Create your first support tier." },
      { emoji: "💎", name: "Founding Rate",   how: "Lock in the 10% founding creator fee — permanent." },
    ]
  },
  {
    label: "Fan achievements",
    emoji: "🌟",
    color: "linear-gradient(135deg,#f97316,#ea580c)",
    description: "Earned by fans who engage deeply with the platform.",
    badges: [
      { emoji: "👁",  name: "First Viewer",   how: "Be among the first 10 viewers on any moment." },
      { emoji: "🌟", name: "Early Fan",       how: "Follow a creator before they reach 100 followers." },
      { emoji: "🔓", name: "First Unlock",    how: "Use credits to unlock your first exclusive moment." },
      { emoji: "⚡", name: "100 Credits",     how: "Spend 100 credits on the platform." },
      { emoji: "🔥", name: "500 Credits",     how: "Spend 500 credits — superfan dedication." },
      { emoji: "🔥", name: "7-Day Streak",    how: "Log in 7 days in a row." },
      { emoji: "🏆", name: "30-Day Streak",   how: "Log in 30 days in a row as a fan." },
    ]
  },
  {
    label: "Secret badges",
    emoji: "🎲",
    color: "linear-gradient(135deg,#7c3aed,#ec4899,#f59e0b)",
    description: "Hidden badges that unlock through unexpected actions. Explore the platform to discover them.",
    badges: [
      { emoji: "🎲", name: "???", how: "This is a secret badge. Keep exploring to discover it." },
      { emoji: "👻", name: "???", how: "This is a secret badge. Something about the time you post..." },
      { emoji: "🦉", name: "???", how: "This is a secret badge. Early risers might find this one." },
      { emoji: "🌊", name: "???", how: "This is a secret badge. Consistency is key." },
    ]
  },
];

const CATEGORIES = [
  {
    id: "moments",
    label: "Moments",
    icon: Eye,
    color: "linear-gradient(135deg,#8b5cf6,#6d28d9)",
    questions: [
      { q: "What is a moment?", a: "A moment is a piece of content posted by a creator — a photo, behind-the-scenes shot, or exclusive update. Moments can be open to everyone, limited to a set number of free views, or exclusive to supporters from the start." },
      { q: "What happens when free views run out?", a: "Once the free view window closes the moment becomes supporter-only. Followers can still unlock it by joining a creator's support tier or spending credits. The creator sets the limit — typically 100 free views." },
      { q: "How does the view counter work?", a: "Every time someone opens a moment it counts as one view. The counter starts at the free view limit and counts down. When it hits zero the moment locks. You can see the counter on every post — green means plenty left, amber means closing soon, red means almost gone." },
      { q: "Can I save a moment to view later?", a: "Yes — hit the bookmark icon on any post to save it. Saved moments appear in your saved posts section. Note that if a moment locks before you view it you'll need supporter access to unlock it." },
    ]
  },
  {
    id: "drops",
    label: "Drops",
    icon: Flame,
    color: "linear-gradient(135deg,#f97316,#ea580c)",
    questions: [
      { q: "What is a drop?", a: "A drop is a scheduled content release. Creators set a future date and time and fans can see the countdown. Drops build anticipation and reward fans who show up early." },
      { q: "What is credit acceleration?", a: "Some creators allow fans to spend their earned credits to move a drop's release date forward. The more credits fans pool together, the earlier the drop releases — up to the maximum the creator allows." },
      { q: "How do I get notified about drops?", a: "Follow a creator and make sure notifications are enabled in your settings. You'll get an alert when a drop goes live. You can also spend credits on a Drop Alert to get notified the instant a specific creator posts." },
    ]
  },
  {
    id: "credits",
    label: "Credits",
    icon: Zap,
    color: "linear-gradient(135deg,#f59e0b,#d97706)",
    questions: [
      { q: "What are credits?", a: "Credits are 100ViewsOnly's engagement currency. You earn them by showing up daily, completing challenges, and supporting creators. You spend them on things like boosting creators, drop alerts, super comments, unlocking profile themes, and app looks." },
      { q: "How do I earn credits?", a: "You earn credits by logging in daily (streak bonuses increase over time), completing weekly and monthly challenges, hitting streak milestones at 7, 14, 30 and 100 days, and through secret challenges that unlock based on your behaviour on the platform." },
      { q: "Can credits unlock paid content for free?", a: "No — and this is intentional. Credits are designed to support creators not replace their income. Credits can boost visibility, accelerate drops, and unlock cosmetics but they never bypass a creator's paywall. Creators always get paid." },
      { q: "Do credits expire?", a: "Credits do not expire as long as your account is active. If your account is inactive for an extended period credits may be subject to platform policy — check the legal page for details." },
    ]
  },
  {
    id: "support",
    label: "Supporting creators",
    icon: Heart,
    color: "linear-gradient(135deg,#ec4899,#be185d)",
    questions: [
      { q: "How do I support a creator?", a: "Visit a creator's profile and click the Support button. You'll see their available tiers with different price points and perks. Choose a tier and complete payment through Stripe. Your support renews monthly until you cancel." },
      { q: "Can I cancel my support?", a: "Yes — you can cancel any time from your account settings. Your access continues until the end of the billing period. There are no cancellation fees or penalties." },
      { q: "What perks do supporters get?", a: "Perks vary by creator and tier. Common perks include early drop access, priority replies, and unlock credits on limited moments. Creators set their own perks." },
      { q: "Are payments secure?", a: "Yes. All payments are processed through Stripe, one of the world's most trusted payment platforms. 100ViewsOnly never stores your card information. Transactions are encrypted and protected." },
    ]
  },
  {
    id: "account",
    label: "Account & safety",
    icon: Shield,
    color: "linear-gradient(135deg,#22c55e,#15803d)",
    questions: [
      { q: "How do I report a creator or content?", a: "Tap the three-dot menu on any post or profile and select Report. Reports are reviewed by our safety team. We take harassment, impersonation, and inappropriate content seriously." },
      { q: "How do I block someone?", a: "Visit their profile, tap the three-dot menu and select Block. Blocked users cannot view your profile, moments, or interact with your content. Blocking is private — they are not notified." },
      { q: "How do I verify my email?", a: "Go to Settings and tap Send verification email. Check your inbox — if it doesn't arrive within a few minutes check your spam folder. Email verification is required to access creator tools." },
    ]
  },
  {
    id: "badges",
    label: "Badges & achievements",
    icon: Trophy,
    color: "linear-gradient(135deg,#f59e0b,#7c3aed)",
    questions: [] // handled separately below
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`help-faq-item ${open ? "faq-open" : ""}`}>
      <button type="button" className="help-faq-question" onClick={() => setOpen(p => !p)}>
        <span>{question}</span>
        {open ? <ChevronUp size={16} strokeWidth={2.5} /> : <ChevronDown size={16} strokeWidth={2.5} />}
      </button>
      {open && <div className="help-faq-answer">{answer}</div>}
    </div>
  );
}

function BadgeCategorySection({ cat }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="help-badge-cat">
      <button type="button" className="help-badge-cat-header" onClick={() => setOpen(p => !p)}>
        <div className="help-badge-cat-icon" style={{ background: cat.color }}>
          <span>{cat.emoji}</span>
        </div>
        <div>
          <strong>{cat.label}</strong>
          <p>{cat.description}</p>
        </div>
        <div className="help-badge-cat-count">{cat.badges.length}</div>
        {open ? <ChevronUp size={15} strokeWidth={2.5} color="var(--muted)" /> : <ChevronDown size={15} strokeWidth={2.5} color="var(--muted)" />}
      </button>
      {open && (
        <div className="help-badge-list">
          {cat.badges.map((b, i) => (
            <div key={i} className="help-badge-row">
              <div className="help-badge-emoji">{b.emoji}</div>
              <div>
                <strong>{b.name}</strong>
                <p>{b.how}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("moments");
  const [search, setSearch] = useState("");

  const current = CATEGORIES.find(c => c.id === activeCategory);
  const isBadgeCategory = activeCategory === "badges";

  const filtered = search.trim()
    ? CATEGORIES.filter(c => c.id !== "badges").flatMap(c => c.questions.filter(q =>
        q.q.toLowerCase().includes(search.toLowerCase()) ||
        q.a.toLowerCase().includes(search.toLowerCase())
      ))
    : current?.questions || [];

  return (
    <div className="help-page">

      <button type="button" className="help-back-btn" onClick={() => navigate(-1)}>
        <ChevronLeft size={18} strokeWidth={2.5} />
        Back
      </button>

      <div className="help-hero">
        <div className="help-hero-icon">
          <BookOpen size={24} color="white" strokeWidth={2} />
        </div>
        <div>
          <p className="exact-eyebrow">Help centre</p>
          <h1>How 100ViewsOnly works</h1>
          <p className="help-hero-sub">Learn about moments, drops, credits, creator support, and account safety.</p>
        </div>
      </div>

      <div className="help-search-wrap">
        <Search size={16} strokeWidth={2} color="var(--muted)" />
        <input
          type="text"
          className="help-search-input"
          placeholder="Search for answers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="help-layout">
        {!search && (
          <aside className="help-sidebar">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`help-cat-btn ${activeCategory === cat.id ? "help-cat-active" : ""}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  <div className="help-cat-icon" style={{ background: cat.color }}>
                    <Icon size={14} strokeWidth={2} color="white" />
                  </div>
                  {cat.label}
                </button>
              );
            })}
            <div className="help-contact-card">
              <strong>Still need help?</strong>
              <p>Our team is here for you. Reach out and we'll get back to you within 24 hours.</p>
              <a href="mailto:support@100viewsonly.ca" className="help-contact-btn">
                Contact support <ArrowRight size={13} strokeWidth={2.5} />
              </a>
            </div>
          </aside>
        )}

        <div className="help-main">
          {!search && current && (
            <div className="help-section-header">
              <div className="help-section-icon" style={{ background: current.color }}>
                <current.icon size={18} strokeWidth={2} color="white" />
              </div>
              <div>
                <h2>{current.label}</h2>
                <p>{isBadgeCategory ? `${BADGE_CATEGORIES.reduce((s,c) => s + c.badges.length, 0)} badges across ${BADGE_CATEGORIES.length} categories` : `${current.questions.length} questions`}</p>
              </div>
            </div>
          )}

          {search && (
            <div className="help-search-header">
              <strong>{filtered.length} results for "{search}"</strong>
            </div>
          )}

          {/* Badge categories view */}
          {!search && isBadgeCategory ? (
            <div className="help-badge-categories">
              <div className="help-badge-intro">
                <p>Badges appear on your profile trophy case and show your history on 100ViewsOnly. Hover any badge on a profile to see what it means. Some badges are secret — explore the platform to discover them.</p>
              </div>
              {BADGE_CATEGORIES.map((cat, i) => (
                <BadgeCategorySection key={i} cat={cat} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="help-empty">
              <Search size={28} strokeWidth={1.5} color="var(--light-muted)" />
              <strong>No results found</strong>
              <p>Try different keywords or browse categories.</p>
            </div>
          ) : (
            <div className="help-faq-list">
              {filtered.map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          )}

          {!search && !isBadgeCategory && (
            <div className="help-quick-links">
              <strong>Related pages</strong>
              <div className="help-quick-row">
                <Link to="/legal" className="help-quick-link">
                  <Shield size={14} strokeWidth={2} />
                  Legal & policies
                  <ArrowRight size={13} strokeWidth={2.5} />
                </Link>
                <Link to="/perks" className="help-quick-link">
                  <Zap size={14} strokeWidth={2} />
                  Your perks & credits
                  <ArrowRight size={13} strokeWidth={2.5} />
                </Link>
                <Link to="/settings" className="help-quick-link">
                  <Shield size={14} strokeWidth={2} />
                  Account settings
                  <ArrowRight size={13} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
